import React from 'react';
import { Link } from 'react-router-dom';
import {
  User,
  LogOut,
  Settings,
  Building2,
  PlusCircle,
  CalendarDays,
  FileEdit,
  Key
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { UserRoles } from '@/contexts/auth/enums';
import { User as UserType } from '@/contexts/auth/types';
import ProfileSidebar from '@/components/profile/ProfileSidebar';

interface UserMenuProps {
  user: UserType | null;
  isAdmin: boolean;
  isOrganizer: boolean;
  logout: () => void;
}

const UserMenu: React.FC<UserMenuProps> = ({ user, isAdmin, isOrganizer, logout }) => {
  if (!user) return null;

  const userFullName = `${user.userProfile.name.lastName || ''} ${user.userProfile.name.firstName || ''}`.trim();

  const getInitials = (name: string) => {
    const parts = name.split(' ').filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return parts[0]?.substring(0, 2).toUpperCase() || '';
  };

  const changePasswordPath = isOrganizer ? "/organizer/change-password" : "/participant/change-password";
  const profilePath = isOrganizer ? "/organizer/profile" : "/participant/profile";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="relative h-10 w-10 rounded-full">
          <Avatar className="h-10 w-10 border border-gray-200">
            <AvatarFallback className="bg-accent text-accent-foreground">
              {getInitials(userFullName)}
            </AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-80" align="end">
        <div className="p-2">
          <ProfileSidebar
            name={userFullName}
            email={user.email}
            role={user.roles[0]}
            contactPhone={user.userProfile?.contactPhone}
            website={user.userProfile?.website}
            companyName={user.userProfile?.companyInfo?.name}
            compact={true}
          />
        </div>

        {isOrganizer && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem asChild>
                <Link to="/organizer/dashboard">
                  <Building2 className="h-4 w-4 mr-2" />
                  <span>Szervező vezérlőpult</span>
                </Link>
              </DropdownMenuItem>
            </DropdownMenuGroup>
          </>
        )}

        <DropdownMenuSeparator />

        <DropdownMenuGroup>
          <DropdownMenuItem asChild>
            <Link to={profilePath}>
              <User className="h-4 w-4 mr-2" />
              <span>Profil kezelése</span>
            </Link>
          </DropdownMenuItem>

          <DropdownMenuItem asChild>
            <Link to={changePasswordPath}>
              <Key className="h-4 w-4 mr-2" />
              <span>Jelszó változtatás</span>
            </Link>
          </DropdownMenuItem>
        </DropdownMenuGroup>


        {isAdmin && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Admin funkciók</DropdownMenuLabel>
            <DropdownMenuGroup>
              <DropdownMenuItem asChild>
                <Link to="/admin/dashboard">
                  <Settings className="h-4 w-4 mr-2" />
                  <span>Admin vezérlőpult</span>
                </Link>
              </DropdownMenuItem>
            </DropdownMenuGroup>
          </>
        )}

        <DropdownMenuSeparator />

        <DropdownMenuItem onClick={logout} className="text-red-600">
          <LogOut className="h-4 w-4 mr-2" />
          <span>Kijelentkezés</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default UserMenu;
